import type { ProductHit } from '@libs/algolia';
import { logger } from '@libs/core';

import { cweaClient } from '../clients/api-proxy-browser-client';
import { CWEA_ENDPOINTS } from '../constants';

/** Matches ProductsByIdsController max batch size in product-catalog-process-api. */
export const MAX_PRODUCT_IDS_PER_REQUEST = 100;

type GetProductsByIdsParams = {
    ids: string[];
    caller?: string;
};

function chunkIds(ids: string[], chunkSize: number): string[][] {
    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += chunkSize) {
        chunks.push(ids.slice(i, i + chunkSize));
    }
    return chunks;
}

async function fetchProductsByIdsBatch(url: string, ids: string[], caller?: string): Promise<ProductHit[]> {
    const result = await cweaClient.post<ProductHit[], { ids: string[] }>(url, {
        body: { ids },
        useOldFetch: true,
        ...(caller ? { headers: { 'x-petm-algolia-caller': caller } } : {}),
    });

    if (!result) {
        logger.error(`[GET PRODUCTS BY IDS][Error]: ${url}`);
        throw new Error(`[GET PRODUCTS BY IDS]: ${url} returned null`);
    }

    return result;
}

/**
 * Batch fetch products by ids (SKU or Web Style ID) via the CWEA DP-2248 cached proxy endpoint.
 * Requests with more than {@link MAX_PRODUCT_IDS_PER_REQUEST} ids are split into parallel batches.
 * @param ids - Array of product SKUs or Web Style IDs
 */
export async function getProductsByIds({ ids, caller }: GetProductsByIdsParams): Promise<ProductHit[]> {
    const url = CWEA_ENDPOINTS.productLookup.getProductsByIds();
    logger.info(`[GET PRODUCTS BY IDS]: ${url}`);

    if (ids.length === 0) {
        return [];
    }

    if (ids.length <= MAX_PRODUCT_IDS_PER_REQUEST) {
        return fetchProductsByIdsBatch(url, ids, caller);
    }

    const batches = chunkIds(ids, MAX_PRODUCT_IDS_PER_REQUEST);
    const results = await Promise.all(batches.map(batch => fetchProductsByIdsBatch(url, batch, caller)));
    return results.flat();
}
